import { useEffect, useState, useMemo } from "react";
import api, { openPdf } from "../lib/api";
import PageHeader from "../components/PageHeader";
import Pagination from "../components/Pagination";
import { StatusBadge } from "../components/Badges";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Search, Download, FileText, AlertTriangle, CheckCircle2, Mail, QrCode, Copy, Send, Eye, FileUp, Ban, RotateCcw } from "lucide-react";
import FilterPills, { useQueryFilter } from "../components/FilterPills";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { API_BASE } from "../lib/api";
import ImportDialog from "../components/ImportDialog";

/**
 * Rechnungsübersicht — filtert delivery_notes serverseitig auf jene, die eine
 * `invoice_number` besitzen. Zeigt Zahlungsstatus + berechnete Mahnstufe
 * (0 = offen im Ziel, 1 = 1. Erinnerung >= Fälligkeit + 7T, 2 = >=14T, 3 = >=30T).
 */
function computeDunning(inv) {
  if (inv.payment_status === "paid") return { level: 0, label: "bezahlt" };
  const due = inv.due_date || inv.invoice_date;
  if (!due) return { level: 0, label: "—" };
  const d = new Date(due);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days < 0) return { level: 0, label: `noch ${-days} T` };
  if (days === 0) return { level: 0, label: "fällig heute" };
  if (days < 7) return { level: 0, label: `${days} T überfällig` };
  if (days < 14) return { level: 1, label: `1. Mahnung (${days} T)` };
  if (days < 30) return { level: 2, label: `2. Mahnung (${days} T)` };
  return { level: 3, label: `3. Mahnung (${days} T)` };
}

export default function Invoices() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [payDlg, setPayDlg] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [payLink, setPayLink] = useState(null);   // {invoice_number, iban, bic, bank_name, account_holder, amount, epc_qr_data_url, paypal_url, paypal_qr_data_url, paypal_handle}
  const [payLinkLoading, setPayLinkLoading] = useState(false);
  const [expMonth, setExpMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [dunPrev, setDunPrev] = useState(null);   // dunning preview data
  const [dunPrevLoading, setDunPrevLoading] = useState(false);
  const [stornoDlg, setStornoDlg] = useState(null); // {row, mode: "storno"|"credit_note", restock, reason}
  const [stornoBusy, setStornoBusy] = useState(false);

  const load = async () => {
    const params = { page, page_size: pageSize, has_invoice: 1 };
    if (q) params.q = q;
    if (status !== "all") params.payment_status = status;
    const r = await api.get("/delivery-notes", { params });
    // Client-Filter falls Backend has_invoice nicht kennt
    const data = (r.data || []).filter((d) => d.invoice_number);
    setRows(data);
    setTotal(Number(r.headers["x-total-count"]) || data.length);
  };

  const markPaid = async () => {
    if (!payDlg) return;
    try {
      await api.patch(`/delivery-notes/${payDlg.id}`, {
        payment_status: "paid",
        paid_at: new Date(payDlg.date + "T12:00:00Z").toISOString(),
        paid_amount: Number(payDlg.amount) || null,
      });
      toast.success(`${payDlg.invoice_number} als bezahlt markiert`);
      setPayDlg(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/exports/invoices.csv", { params: { month: expMonth }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a"); a.href = url; a.download = `rechnungen-${expMonth}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`CSV ${expMonth} exportiert`);
    } catch { toast.error("Export fehlgeschlagen"); }
  };

  const openPayLink = async (row) => {
    setPayLinkLoading(true);
    try {
      const { data } = await api.get(`/delivery-notes/${row.id}/payment-info`);
      setPayLink({ ...data, id: row.id, customer_name: row.customer_name });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Zahlungslink nicht verfügbar");
    } finally { setPayLinkLoading(false); }
  };

  const openDunningPreview = async (row, level) => {
    setDunPrevLoading(true);
    try {
      const { data } = await api.get(`/delivery-notes/${row.id}/dunning-preview`, { params: { level } });
      setDunPrev({ ...data, id: row.id });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Vorschau nicht verfügbar");
    } finally { setDunPrevLoading(false); }
  };

  const copyToClipboard = async (text, label) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} kopiert`);
    } catch { toast.error("Kopieren fehlgeschlagen"); }
  };

  const submitStorno = async () => {
    if (!stornoDlg) return;
    const { row, mode, restock, reason_code, reason_note } = stornoDlg;
    if (!reason_code) { toast.error("Bitte einen Grund auswählen"); return; }
    setStornoBusy(true);
    try {
      const endpoint = mode === "credit_note" ? "credit-note" : "storno";
      const { data } = await api.post(`/delivery-notes/${row.id}/${endpoint}`, {
        restock: !!restock,
        reason_code,
        reason_note: (reason_note || "").trim() || null,
      });
      toast.success(
        `${mode === "credit_note" ? "Gutschrift" : "Storno"} ${data.invoice_number} erstellt${restock ? " · Ware zurückgebucht" : ""}`
      );
      setStornoDlg(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Storno fehlgeschlagen");
    } finally { setStornoBusy(false); }
  };

  useEffect(() => { load(); }, [page, pageSize, q, status]); // eslint-disable-line

  const fmt = (v) => (v || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

  const totals = useMemo(() => {
    let open = 0, paid = 0, overdue = 0;
    for (const r of rows) {
      const t = Number(r.total_gross || r.total_net || 0);
      if (r.payment_status === "paid") paid += t;
      else if (r.payment_status === "cancelled" || r.is_storno || r.is_credit_note) {
        // storno / credit / cancelled: does not count toward open or overdue
        continue;
      } else {
        open += t;
        const dun = computeDunning(r);
        if (dun.level >= 1) overdue += t;
      }
    }
    return { open, paid, overdue };
  }, [rows]);

  return (
    <div data-testid="page-invoices">
      <PageHeader
        eyebrow="Buchhaltung"
        title="Rechnungen"
        description="Ausgestellte Rechnungen mit Zahlungsstatus und automatischer Mahnstufen-Berechnung."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Input type="month" value={expMonth} onChange={(e) => setExpMonth(e.target.value)}
                   data-testid="input-export-month"
                   className="h-9 w-40" />
            <Button onClick={exportCsv} variant="outline" data-testid="btn-export-csv"
                    className="rounded-full">
              <Download size={14} className="mr-1" /> CSV Steuerberater
            </Button>
            <Button variant="outline" data-testid="btn-import-invoices"
                    onClick={() => setImportOpen(true)}
                    className="rounded-full">
              <FileUp size={14} className="mr-1" /> Importieren
            </Button>
          </div>
        }
      />

      {/* KPI-Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-[var(--border)] rounded-2xl p-5" data-testid="kpi-open">
          <div className="overline text-[var(--text-muted)]">Offen</div>
          <div className="font-display font-black text-3xl mt-1">{fmt(totals.open)}</div>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-2xl p-5" data-testid="kpi-overdue">
          <div className="overline text-red-700 inline-flex items-center gap-1"><AlertTriangle size={12} /> Überfällig (Mahnung)</div>
          <div className="font-display font-black text-3xl mt-1 text-red-700">{fmt(totals.overdue)}</div>
        </div>
        <div className="bg-green-50 border border-green-200 rounded-2xl p-5" data-testid="kpi-paid">
          <div className="overline text-green-700">Bezahlt (aktuelle Seite)</div>
          <div className="font-display font-black text-3xl mt-1 text-green-700">{fmt(totals.paid)}</div>
        </div>
      </div>

      <div className="bg-white border border-[var(--border)] rounded-2xl overflow-hidden">
        <div className="p-3 border-b border-[var(--border)] flex items-center gap-2 flex-wrap">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input placeholder="RE-Nr., Kunde…" value={q} onChange={(e) => setQ(e.target.value)}
                   className="pl-8 rounded-xl h-9" data-testid="input-invoice-search" />
          </div>
          <FilterPills
            options={[["all", "Alle", "#e5e5e5"], ["open", "Offen", "#fde68a"], ["paid", "Bezahlt", "#d1fae5"]]}
            value={status}
            onChange={setStatus}
          />
          <div className="text-xs text-[var(--text-muted)] ml-auto">{rows.length}/{total}</div>
        </div>

        {/* Desktop-Tabelle */}
        <div className="hidden md:block">
          <Table className="table-zebra">
            <TableHeader>
              <TableRow>
                <TableHead className="overline">RE-Nr.</TableHead>
                <TableHead className="overline">LS-Nr.</TableHead>
                <TableHead className="overline">Kunde</TableHead>
                <TableHead className="overline">RE-Datum</TableHead>
                <TableHead className="overline">Fälligkeit</TableHead>
                <TableHead className="overline text-right">Betrag</TableHead>
                <TableHead className="overline">Mahnstufe</TableHead>
                <TableHead className="overline text-right">Aktion</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => {
                const dun = computeDunning(r);
                const colour = { 1: "#ea580c", 2: "#dc2626", 3: "#7f1d1d" }[dun.level];
                return (
                  <TableRow key={r.id} data-testid={`invoice-row-${r.invoice_number}`}>
                    <TableCell className="font-mono font-bold">
                      {r.is_storno && <span className="pop-pill bg-red-100 text-red-700 text-[9px] font-bold mr-1">STORNO</span>}
                      {r.is_credit_note && <span className="pop-pill bg-purple-100 text-purple-700 text-[9px] font-bold mr-1">GUTSCHRIFT</span>}
                      {r.invoice_number}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.number}</TableCell>
                    <TableCell>{r.customer_name}</TableCell>
                    <TableCell className="font-mono text-xs">{(r.invoice_date || "").slice(0, 10)}</TableCell>
                    <TableCell className="font-mono text-xs">{(r.due_date || "").slice(0, 10) || "—"}</TableCell>
                    <TableCell className="text-right font-mono text-sm font-semibold">
                      {fmt(r.total_gross || r.total_net)}
                    </TableCell>
                    <TableCell>
                      {r.payment_status === "paid"
                        ? <span className="pop-pill bg-green-100 text-green-800 text-[10px] font-bold">✓ BEZAHLT</span>
                        : r.payment_status === "cancelled"
                        ? <span className="pop-pill bg-gray-200 text-gray-700 text-[10px] font-bold">STORNIERT</span>
                        : <span className="pop-pill text-[10px] font-bold"
                                style={colour ? { background: colour + "20", color: colour } : { background: "#f3f4f6", color: "#6b7280" }}>
                            {dun.label}
                          </span>}
                    </TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <button data-testid={`btn-pdf-${r.invoice_number}`}
                              onClick={() => openPdf(`/delivery-notes/${r.id}/invoice.pdf`).catch(() => toast.error("PDF Fehler"))}
                              className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-pink)] hover:underline mr-3">
                        <FileText size={14} /> PDF
                      </button>
                      {r.payment_status !== "paid" && r.payment_status !== "cancelled" && !r.is_storno && !r.is_credit_note && (
                        <button data-testid={`btn-paylink-${r.invoice_number}`}
                                onClick={() => openPayLink(r)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-black)] hover:underline mr-3">
                          <QrCode size={14} /> Zahlungslink
                        </button>
                      )}
                      {dun.level >= 1 && r.payment_status !== "cancelled" && !r.is_storno && !r.is_credit_note && (
                        <button data-testid={`btn-dunning-preview-${r.invoice_number}`}
                                onClick={() => openDunningPreview(r, dun.level)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-red-700 hover:underline mr-3">
                          <Eye size={14} /> Mahnung
                        </button>
                      )}
                      {r.payment_status !== "paid" && r.payment_status !== "cancelled" && !r.is_storno && !r.is_credit_note && (
                        <button data-testid={`btn-pay-${r.invoice_number}`}
                                onClick={() => setPayDlg({ id: r.id, invoice_number: r.invoice_number, amount: r.total_gross || r.total_net || 0, date: new Date().toISOString().slice(0, 10) })}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-[var(--success)] hover:underline mr-3">
                          <CheckCircle2 size={14} /> Bezahlt
                        </button>
                      )}
                      {!r.is_storno && !r.is_credit_note && r.payment_status !== "cancelled" && (
                        <>
                          <button data-testid={`btn-storno-${r.invoice_number}`}
                                  onClick={() => setStornoDlg({ row: r, mode: "storno", restock: true, reason_code: "", reason_note: "" })}
                                  className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:underline mr-3">
                            <Ban size={14} /> Storno
                          </button>
                          <button data-testid={`btn-credit-${r.invoice_number}`}
                                  onClick={() => setStornoDlg({ row: r, mode: "credit_note", restock: true, reason_code: "", reason_note: "" })}
                                  className="inline-flex items-center gap-1 text-xs font-semibold text-purple-700 hover:underline">
                            <RotateCcw size={14} /> Gutschrift
                          </button>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-10 text-[var(--text-muted)]">Keine Rechnungen.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>

        {/* Mobile-Karten */}
        <div className="md:hidden divide-y divide-[var(--border)]" data-testid="invoices-mobile-cards">
          {rows.map((r) => {
            const dun = computeDunning(r);
            const colour = { 1: "#ea580c", 2: "#dc2626", 3: "#7f1d1d" }[dun.level];
            return (
              <div key={r.id} className="p-4" data-testid={`invoice-card-${r.invoice_number}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-mono font-bold">{r.invoice_number}</div>
                    <div className="text-sm truncate">{r.customer_name}</div>
                    <div className="text-xs text-[var(--text-muted)] font-mono">
                      {(r.invoice_date || "").slice(0, 10)} · {fmt(r.total_gross || r.total_net)}
                    </div>
                  </div>
                  {r.payment_status === "paid"
                    ? <span className="pop-pill bg-green-100 text-green-800 text-[10px] font-bold flex-shrink-0">✓</span>
                    : <span className="pop-pill text-[10px] font-bold flex-shrink-0"
                            style={colour ? { background: colour + "20", color: colour } : { background: "#f3f4f6", color: "#6b7280" }}>
                        {dun.label}
                      </span>}
                </div>
                <button onClick={() => openPdf(`/delivery-notes/${r.id}/invoice.pdf`).catch(() => toast.error("PDF Fehler"))}
                        className="w-full rounded-full border border-[var(--border)] py-2 text-sm font-semibold inline-flex items-center justify-center gap-2">
                  <Download size={14} /> Rechnung öffnen
                </button>
              </div>
            );
          })}
        </div>

        <Pagination page={page} pageSize={pageSize} total={total}
                    onChange={setPage} onPageSizeChange={(n) => { setPage(1); setPageSize(n); }}
                    storageKey="invoices" testIdPrefix="invoices-pager" />
      </div>

      {/* Zahlungslink Dialog */}
      <Dialog open={!!payLink} onOpenChange={(o) => !o && setPayLink(null)}>
        <DialogContent className="max-w-2xl" data-testid="dialog-payment-link">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <QrCode size={22} className="text-[var(--pop-pink)]" />
              Zahlungslink — {payLink?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {payLink && (
            <div className="space-y-4">
              <div className="text-sm text-[var(--text-muted)]">
                Kunde: <span className="font-semibold text-[var(--pop-black)]">{payLink.customer_name}</span>
                {" · "}Betrag: <span className="font-mono font-bold text-[var(--pop-black)]">{fmt(payLink.amount)}</span>
              </div>

              {!payLink.iban && !payLink.paypal_url && (
                <div className="rounded-xl border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
                  Keine Zahlungsmethode konfiguriert. Bitte in <b>Einstellungen → Firma & Absender</b> IBAN und/oder PayPal.me-Handle hinterlegen.
                </div>
              )}

              <div className="grid md:grid-cols-2 gap-4">
                {/* EPC / Girocode */}
                {payLink.iban && (
                  <div className="border border-[var(--border)] rounded-2xl p-4">
                    <div className="font-semibold mb-2 flex items-center gap-2">
                      <span className="pop-pill bg-[var(--pop-yellow)] text-black text-[10px] font-bold">SEPA</span>
                      Girocode (Banking-App)
                    </div>
                    {payLink.epc_qr_data_url && (
                      <img src={payLink.epc_qr_data_url} alt="Girocode QR"
                           data-testid="img-epc-qr"
                           className="w-40 h-40 mx-auto bg-white p-1 rounded-lg" />
                    )}
                    <div className="text-xs font-mono mt-3 space-y-1">
                      <div><span className="text-[var(--text-muted)]">IBAN:</span> {payLink.iban}</div>
                      <div><span className="text-[var(--text-muted)]">BIC:</span> {payLink.bic || "—"}</div>
                      <div><span className="text-[var(--text-muted)]">Zweck:</span> {payLink.reference}</div>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <Button variant="outline" size="sm" className="rounded-full text-xs"
                              data-testid="btn-copy-iban"
                              onClick={() => copyToClipboard(payLink.iban, "IBAN")}>
                        <Copy size={12} className="mr-1" /> IBAN
                      </Button>
                    </div>
                  </div>
                )}

                {/* PayPal.me */}
                {payLink.paypal_url && (
                  <div className="border border-[var(--border)] rounded-2xl p-4">
                    <div className="font-semibold mb-2 flex items-center gap-2">
                      <span className="pop-pill bg-[#003087] text-white text-[10px] font-bold">PAYPAL</span>
                      PayPal.me (1-Klick)
                    </div>
                    {payLink.paypal_qr_data_url && (
                      <img src={payLink.paypal_qr_data_url} alt="PayPal QR"
                           data-testid="img-paypal-qr"
                           className="w-40 h-40 mx-auto bg-white p-1 rounded-lg" />
                    )}
                    <div className="text-xs font-mono mt-3 break-all">
                      <a href={payLink.paypal_url} target="_blank" rel="noreferrer"
                         className="text-[#003087] underline"
                         data-testid="link-paypal">
                        {payLink.paypal_url}
                      </a>
                    </div>
                    <div className="flex gap-1 mt-2">
                      <Button variant="outline" size="sm" className="rounded-full text-xs"
                              data-testid="btn-copy-paypal"
                              onClick={() => copyToClipboard(payLink.paypal_url, "PayPal-Link")}>
                        <Copy size={12} className="mr-1" /> Link
                      </Button>
                      <Button variant="outline" size="sm" className="rounded-full text-xs"
                              onClick={() => window.open(payLink.paypal_url, "_blank")}>
                        <Send size={12} className="mr-1" /> Öffnen
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="rounded-xl bg-[var(--pop-cream)]/40 p-3 text-xs text-[var(--text-muted)]">
                Tipp: Diese QR-Codes werden automatisch auch auf der Rechnung <b>und</b> auf jeder Mahnung gedruckt — der Kunde muss den Betrag nicht mehr abtippen.
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setPayLink(null)} className="rounded-full">Schließen</Button>
              </DialogFooter>
            </div>
          )}
          {!payLink && payLinkLoading && <div className="p-6 text-center text-[var(--text-muted)]">Lade…</div>}
        </DialogContent>
      </Dialog>

      {/* Mahnungs-Vorschau Dialog */}
      <Dialog open={!!dunPrev} onOpenChange={(o) => !o && setDunPrev(null)}>
        <DialogContent className="max-w-xl" data-testid="dialog-dunning-preview">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2" style={{ color: dunPrev?.colour }}>
              <Mail size={22} />
              {dunPrev?.title} — {dunPrev?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {dunPrev && (
            <div className="space-y-4">
              <div className="rounded-xl border-2 p-4 text-sm space-y-2" style={{ borderColor: dunPrev.colour + "40" }}>
                <div className="flex items-center justify-between">
                  <span className="text-[var(--text-muted)]">Kunde</span>
                  <span className="font-semibold">{dunPrev.customer_name} · {dunPrev.customer_type}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[var(--text-muted)]">Rechnungsdatum · Fälligkeit</span>
                  <span className="font-mono text-xs">{dunPrev.invoice_date} · {dunPrev.due_date || "—"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-[var(--text-muted)]">Verzugstage</span>
                  <span className="pop-pill text-xs font-bold" style={{ background: dunPrev.colour + "20", color: dunPrev.colour }} data-testid="dun-days-overdue">
                    {dunPrev.days_overdue} Tage
                  </span>
                </div>
              </div>

              <div className="rounded-xl border border-[var(--border)] p-4 space-y-2 text-sm">
                <div className="overline text-xs mb-1">Aufstellung nach § 288 BGB</div>
                <div className="flex items-center justify-between">
                  <span>Rechnungsbetrag (brutto)</span>
                  <span className="font-mono" data-testid="dun-invoice-gross">{fmt(dunPrev.invoice_gross)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Mahngebühr (Stufe {dunPrev.level})</span>
                  <span className="font-mono" data-testid="dun-fee">{fmt(dunPrev.fee)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-[var(--text-muted)]">
                  <span>Verzugszinsen {(dunPrev.yearly_rate * 100).toFixed(2)} % p.a. × {dunPrev.days_overdue} T</span>
                  <span className="font-mono" data-testid="dun-interest">{fmt(dunPrev.interest)}</span>
                </div>
                <div className="border-t border-[var(--border)] pt-2 flex items-center justify-between font-bold text-lg">
                  <span>Gesamt zahlbar</span>
                  <span className="font-mono" style={{ color: dunPrev.colour }} data-testid="dun-total">{fmt(dunPrev.total)}</span>
                </div>
              </div>

              <div className="text-xs text-[var(--text-muted)]">
                Tipp: Prüfe die Zahlen — bezahlt der Kunde vor Absendung noch heute, wird die Mahnung
                überflüssig. Der Girocode/PayPal-QR im PDF trägt automatisch den Gesamtbetrag inkl. Mahngebühr.
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDunPrev(null)} className="rounded-full">Schließen</Button>
                <Button onClick={() => { openPdf(`/delivery-notes/${dunPrev.id}/dunning.pdf?level=${dunPrev.level}`).catch(() => toast.error("Mahnung Fehler")); setDunPrev(null); }}
                        data-testid="btn-open-dunning-pdf"
                        className="rounded-full font-bold text-white"
                        style={{ background: dunPrev.colour }}>
                  <FileText size={14} className="mr-1" /> PDF öffnen
                </Button>
              </DialogFooter>
            </div>
          )}
          {!dunPrev && dunPrevLoading && <div className="p-6 text-center text-[var(--text-muted)]">Lade…</div>}
        </DialogContent>
      </Dialog>

      {/* Zahlung erfassen Dialog */}
      <Dialog open={!!payDlg} onOpenChange={(o) => !o && setPayDlg(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-mark-paid">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2">
              <CheckCircle2 size={22} className="text-[var(--success)]" />
              Zahlung erfassen — {payDlg?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {payDlg && (
            <div className="space-y-4">
              <div>
                <Label>Zahlungsdatum</Label>
                <Input type="date" value={payDlg.date}
                       data-testid="input-pay-date"
                       onChange={(e) => setPayDlg({ ...payDlg, date: e.target.value })} />
              </div>
              <div>
                <Label>Zahlbetrag (€)</Label>
                <Input type="number" step="0.01" value={payDlg.amount}
                       data-testid="input-pay-amount"
                       onChange={(e) => setPayDlg({ ...payDlg, amount: e.target.value })} />
                <div className="text-xs text-[var(--text-muted)] mt-1">
                  Vorschlag: Rechnungsbetrag. Bei Teilzahlung Betrag anpassen — Restbetrag bleibt offen.
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setPayDlg(null)} className="rounded-full">Abbrechen</Button>
                <Button onClick={markPaid} data-testid="btn-confirm-paid"
                        className="rounded-full bg-[var(--success)] text-white hover:opacity-90 font-bold">
                  Als bezahlt markieren
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Storno / Gutschrift Dialog */}
      <Dialog open={!!stornoDlg} onOpenChange={(o) => !o && !stornoBusy && setStornoDlg(null)}>
        <DialogContent className="max-w-md" data-testid="dialog-storno">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl flex items-center gap-2"
                         style={{ color: stornoDlg?.mode === "credit_note" ? "#7c3aed" : "#dc2626" }}>
              {stornoDlg?.mode === "credit_note" ? <RotateCcw size={22} /> : <Ban size={22} />}
              {stornoDlg?.mode === "credit_note" ? "Gutschrift" : "Storno"} — {stornoDlg?.row?.invoice_number}
            </DialogTitle>
          </DialogHeader>
          {stornoDlg && (
            <div className="space-y-4">
              <div className="text-sm text-[var(--text-muted)]">
                Kunde: <span className="font-semibold text-[var(--pop-black)]">{stornoDlg.row.customer_name}</span>
                {" · "}Betrag: <span className="font-mono font-bold text-[var(--pop-black)]">
                  {fmt(stornoDlg.row.total_gross || stornoDlg.row.total_net)}
                </span>
              </div>
              <div className="rounded-xl border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900">
                {stornoDlg.mode === "credit_note"
                  ? <>Es wird eine <b>Gutschrift</b> mit negativen Positionen erzeugt („GUTSCHRIFT zu {stornoDlg.row.invoice_number}"). Die Originalrechnung bleibt bestehen, wird aber als <b>storniert</b> markiert (GoBD-konform).</>
                  : <>Es wird eine <b>Stornorechnung</b> mit negativen Positionen erzeugt („STORNO zu {stornoDlg.row.invoice_number}"). Die Originalrechnung bleibt bestehen, wird aber als <b>storniert</b> markiert (GoBD-konform).</>}
              </div>
              <label className="flex items-start gap-2 cursor-pointer" data-testid="storno-restock-toggle">
                <input type="checkbox" checked={stornoDlg.restock}
                       onChange={(e) => setStornoDlg({ ...stornoDlg, restock: e.target.checked })}
                       className="mt-1" />
                <div className="text-sm">
                  <div className="font-semibold">Ware ins Lager zurückbuchen</div>
                  <div className="text-xs text-[var(--text-muted)]">
                    Erstellt für jede Position eine Wareneingangs-Bewegung (Typ „in") auf die
                    ursprüngliche Charge, sofern vorhanden.
                  </div>
                </div>
              </label>
              <div>
                <Label>Grund <span className="text-red-600">*</span></Label>
                <select value={stornoDlg.reason_code || ""}
                        data-testid="select-storno-reason"
                        onChange={(e) => setStornoDlg({ ...stornoDlg, reason_code: e.target.value })}
                        className="w-full h-10 rounded-lg border border-[var(--border)] bg-white px-3 text-sm">
                  <option value="" disabled>— bitte wählen —</option>
                  <option value="falschlieferung">Falschlieferung</option>
                  <option value="reklamation">Reklamation</option>
                  <option value="preisminderung">Preisminderung</option>
                  <option value="retoure">Retoure</option>
                  <option value="kundenwiderruf">Kundenwiderruf</option>
                  <option value="sonstiges">Sonstiges</option>
                </select>
              </div>
              <div>
                <Label>Notiz (optional)</Label>
                <Input value={stornoDlg.reason_note || ""}
                       data-testid="input-storno-reason-note"
                       placeholder="z. B. Charge kontaminiert, Kunde reklamierte 2 Stk. …"
                       onChange={(e) => setStornoDlg({ ...stornoDlg, reason_note: e.target.value })} />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setStornoDlg(null)}
                        disabled={stornoBusy} className="rounded-full">Abbrechen</Button>
                <Button onClick={submitStorno}
                        disabled={stornoBusy || !stornoDlg.reason_code}
                        data-testid="btn-confirm-storno"
                        className="rounded-full font-bold text-white"
                        style={{ background: stornoDlg.mode === "credit_note" ? "#7c3aed" : "#dc2626" }}>
                  {stornoBusy ? "Erstelle…" : (stornoDlg.mode === "credit_note" ? "Gutschrift erstellen" : "Storno erstellen")}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen}
        entityLabel="Rechnungen"
        previewEndpoint="/invoices/import/preview"
        commitEndpoint="/invoices/import/commit"
        templateUrl={`${API_BASE}/invoices/import/template.xlsx`}
        previewKey="rows" onDone={() => load()}
        renderPreviewHeader={() => (<>
          <th className="text-left p-2">RE-Nr.</th>
          <th className="text-left p-2">Datum</th>
          <th className="text-left p-2">Kunde</th>
          <th className="text-right p-2">Positionen</th>
          <th className="text-right p-2">Betrag</th>
          <th className="text-left p-2">Aktion</th>
        </>)}
        renderPreviewRow={(row, i) => {
          const b = row.action === "create" ? "bg-green-100 text-green-700"
                  : row.action === "update" ? "bg-blue-100 text-blue-700"
                  : "bg-red-100 text-red-700";
          return (
            <tr key={i} className="border-t border-[var(--border)]">
              <td className="p-2 font-mono font-bold">{row.invoice_number}</td>
              <td className="p-2 text-xs">{row.invoice_date || "—"}</td>
              <td className="p-2 text-xs">{row.customer || "—"}</td>
              <td className="p-2 text-right font-mono">{row.line_count}</td>
              <td className="p-2 text-right font-mono">{(row.total || 0).toLocaleString("de-DE",{style:"currency",currency:"EUR"})}</td>
              <td className="p-2"><span className={`pop-pill text-[10px] font-bold ${b}`}>{row.action}</span></td>
            </tr>
          );
        }} />
    </div>
  );
}
